// 2026-10-04 addition, revised same day — see ADMIN_RCM_CONSOLIDATION_PLAN.md.
// Clinician-side RCM table: filters by practice/NPI/name. Separate
// component from RcmPatientPanel per explicit instruction. Revision adds
// field tooltips + real NPI validation, same as RcmPatientPanel's own
// revision — flagged directly as missing from the first pass.
import React, { useState, useEffect, useCallback } from "react";
import { extractErrorDetail } from "./validators";
import {
  Box, Typography, TextField, Button, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import { formatEST } from "../dateUtils";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const B = { navy: "#00357a", red: "#c0392b" };

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

const NPI_RE = /^\d{10}$/;

function FieldWithHelp({ label, help, error, children }) {
  return (
    <Box sx={{ minWidth: 150 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: error ? B.red : "text.secondary", display: "flex", alignItems: "center", gap: 0.4, mb: 0.4 }}>
        {label}
        <Tooltip title={help} arrow placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 13.5, color: "text.disabled", cursor: "help" }} />
        </Tooltip>
      </Typography>
      {children}
      {error && <Typography sx={{ fontSize: 12, color: B.red, mt: 0.2 }}>{error}</Typography>}
    </Box>
  );
}

export default function RcmClinicianPanel({ isDark }) {
  const [filters, setFilters] = useState({ practice_name: "", npi: "", first_name: "", last_name: "" });
  const [npiError, setNpiError] = useState("");
  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === "npi") setNpiError(value && !NPI_RE.test(value) ? "NPI is 10 digits" : "");
  };

  const fetchClinicians = useCallback(async () => {
    if (npiError) return;
    setLoading(true); setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await axios.get(`${API}/api/admin/rcm/clinicians`, { params, headers: authHeaders() });
      // 2026-10-04 fix — same real bug as RcmPatientPanel.js: a 200 OK
      // with {clinicians: [], error: "..."} was silently treated as zero
      // results, discarding the real reason.
      if (res.data.error) {
        setError(res.data.error);
        setClinicians([]);
      } else {
        setClinicians(res.data.clinicians || []);
      }
    } catch (err) {
      setError(extractErrorDetail(err, "Failed to load clinicians."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { fetchClinicians(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    const headers = ["First Name", "Last Name", "Email", "Practice", "NPI", "Registered"];
    const rows = clinicians.map((c) => [c.first_name, c.last_name, c.email, c.practice_name, c.npi, formatEST(c.created_at, { dateOnly: true })]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `clinicians_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.82rem", bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff" } };

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2.5, alignItems: "flex-start" }}>
        <FieldWithHelp label="Practice Name" help="Partial, case-insensitive match against the clinician's registered practice/organization name.">
          <TextField size="small" value={filters.practice_name} onChange={(e) => updateFilter("practice_name", e.target.value)} sx={{ ...fieldSx, minWidth: 190 }} />
        </FieldWithHelp>
        <FieldWithHelp label="NPI" help="10-digit National Provider Identifier — exact match." error={npiError}>
          <TextField size="small" placeholder="10 digits" value={filters.npi} onChange={(e) => updateFilter("npi", e.target.value)}
            error={!!npiError} sx={{ ...fieldSx, minWidth: 140 }} />
        </FieldWithHelp>
        <FieldWithHelp label="First Name" help="Partial, case-insensitive match.">
          <TextField size="small" value={filters.first_name} onChange={(e) => updateFilter("first_name", e.target.value)} sx={{ ...fieldSx, minWidth: 140 }} />
        </FieldWithHelp>
        <FieldWithHelp label="Last Name" help="Partial, case-insensitive match.">
          <TextField size="small" value={filters.last_name} onChange={(e) => updateFilter("last_name", e.target.value)} sx={{ ...fieldSx, minWidth: 140 }} />
        </FieldWithHelp>
        <Box sx={{ display: "flex", gap: 1, alignSelf: "flex-end" }}>
          <Button variant="contained" size="small" onClick={fetchClinicians} disabled={loading || !!npiError}
            sx={{ bgcolor: B.navy, textTransform: "none", fontWeight: 600, height: 40 }}>
            {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Search"}
          </Button>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={exportCsv} disabled={!clinicians.length}
            sx={{ borderColor: B.navy, color: B.navy, textTransform: "none", fontWeight: 600, height: 40 }}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {error && <Typography sx={{ color: B.red, fontSize: 14.5, mb: 1.5 }}>{error}</Typography>}

      <Box sx={{ overflowX: "auto", borderRadius: "12px", border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,53,122,0.1)"}` }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc" }}>
              {["Name", "Email", "Practice", "NPI", "Registered"].map((h) => (
                <TableCell key={h} sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {clinicians.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.disabled", fontSize: 14.5 }}>No clinicians match these filters.</TableCell></TableRow>
            ) : clinicians.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ fontSize: 14 }}>{(c.first_name || c.last_name) ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : "—"}</TableCell>
                <TableCell sx={{ fontSize: 13.5, fontFamily: "monospace" }}>{c.email}</TableCell>
                <TableCell sx={{ fontSize: 13.5 }}>{c.practice_name || "—"}</TableCell>
                <TableCell sx={{ fontSize: 13.5 }}>{c.npi || "—"}</TableCell>
                <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>
                  {formatEST(c.created_at, { dateOnly: true })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
